const WORD_CHAR = /[\p{L}\p{N}_]/u

/**
 * Expands a DOM Range outward so both ends sit on word boundaries: a boundary that lands in the
 * middle of a word moves outward to include the whole word, and boundaries sitting on whitespace
 * shrink inward past it first. Only boundaries inside TEXT nodes are adjusted — element boundaries
 * are left untouched. The range is mutated in place.
 *
 * @param {Range} range - The selection range to adjust.
 * @returns {boolean} true when the range was changed.
 */
export function expandRangeToWordBoundaries(range) {
    if (!range || range.collapsed) return false
    const sameNode = range.startContainer === range.endContainer
    let changed = false

    if (isTextNode(range.startContainer)) {
        const text = range.startContainer.textContent ?? ""
        const limit = sameNode ? range.endOffset : text.length
        let offset = range.startOffset
        while (offset < limit && isWhitespace(text[offset])) offset++
        while (offset > 0 && isWordChar(text[offset - 1]) && isWordChar(text[offset])) offset--
        if (offset !== range.startOffset && (!sameNode || offset < range.endOffset)) {
            range.setStart(range.startContainer, offset)
            changed = true
        }
    }

    if (isTextNode(range.endContainer)) {
        const text = range.endContainer.textContent ?? ""
        const floor = sameNode ? range.startOffset : 0
        let offset = range.endOffset
        while (offset > floor && isWhitespace(text[offset - 1])) offset--
        while (offset < text.length && isWordChar(text[offset - 1]) && isWordChar(text[offset])) offset++
        if (offset !== range.endOffset && (!sameNode || offset > range.startOffset)) {
            range.setEnd(range.endContainer, offset)
            changed = true
        }
    }

    return changed
}

function isTextNode(node) {
    return node?.nodeType === Node.TEXT_NODE
}

function isWordChar(character) {
    return !!character && WORD_CHAR.test(character)
}

function isWhitespace(character) {
    return !!character && /\s/.test(character)
}
